import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';

import { Colors } from '../config/default';
import { commitmentPosition as labels } from '../constants/appConstants';
import { formatAndFloatNumber } from '../services/common';

/**
 * What is actually left on a purchase order or subcontract.
 *
 * The customer report this answers: a PO showing $9,000 remaining — which was
 * only ever the amount the PO was written FOR — while the ERP showed $1,852.31
 * left. The backend now derives the real figure from the ERP's own AP invoice
 * ledger and stores it on the commitment.
 *
 * TWO FIGURES, LABELLED, never blended into one. The ERP's invoices and the ones
 * approved here overlap without either containing the other: some are entered
 * straight into the ERP and Beiing Human never sees them, and some are approved
 * here and not yet exported. Adding them double-counts everything that completed
 * the round trip; showing only one hides real money. So the reader gets both and
 * can see the gap — which for a customer with years of ERP history predating
 * this app is exactly the number they are trying to reconcile.
 *
 * A figure we could not produce renders as an em dash WITH ITS REASON, never as
 * `0`. "Nothing has been invoiced against this commitment" and "the ERP's
 * invoice history has not synced" look identical as a zero, and only one of them
 * is safe to act on.
 */
const amountOrDash = (value) => {
  if (value === null || value === undefined || String(value).trim() === '') {
    return null;
  }
  return formatAndFloatNumber(String(value).replace(/,/g, ''));
};

const Figure = ({ label, value, emphasis = false, hint = '' }) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '.4rem', minWidth: 0 }}>
    <Typography
      sx={{ color: Colors.TEXTGREY, fontSize: '.72rem', whiteSpace: 'nowrap' }}>
      {label}
    </Typography>
    <Typography
      sx={{
        color: emphasis ? Colors.WHITE : Colors.TEXTGREY,
        fontSize: emphasis ? '.95rem' : '.8rem',
        fontWeight: emphasis ? 600 : 400,
        whiteSpace: 'nowrap'
      }}>
      {value}
    </Typography>
    {hint ? (
      <Tooltip title={hint} arrow>
        <InfoOutlined sx={{ color: Colors.TEXTGREY, fontSize: '.85rem' }} />
      </Tooltip>
    ) : null}
  </Box>
);

const CommitmentPositionPanel = ({ position }) => {
  // Absent for every non-Foundation ERP — Vista and Spectrum report their own
  // received/remaining from the ERP directly, so there is nothing to add.
  if (!position) return null;

  // The ERP's own position keeps the existing field names — the same ones the
  // sync writes and every comparison reads.
  const remaining = amountOrDash(position?.remainingAmt);
  const received = amountOrDash(position?.receivedAmt);
  // What Beiing Human has consumed, OPENING FROM THE ERP'S FIGURE — the same
  // rule the backend's calculation applies (`bhReceivedAmt ?? receivedAmt`), so
  // a commitment this app has never drawn against reads as standing exactly
  // where the ERP has it, which is the truth about it.
  //
  // Note what this deliberately does NOT do: it never opens from a figure the
  // ERP does not know. When the ERP side is a dash — the ledger has not synced,
  // the commitment predates the window, the lookup failed — `remaining` is null
  // and BH's stays null too, so the panel goes on saying "nobody managed to
  // look" instead of inventing a position out of one.
  const bhRemaining = amountOrDash(position?.bhRemainingAmt) ?? remaining;
  const bhReceived = amountOrDash(position?.bhReceivedAmt) ?? received;
  const retainage = amountOrDash(position?.erpRetainageAmt);
  const basis = position?.erpConsumptionBasis;
  const reason = labels.REASONS[basis] || '';
  // Change orders. Shown only when there are some, so a commitment without any
  // renders exactly as before. The remaining figures above already count them —
  // these say what they were measured against.
  const changeOrderCount = Number(position?.changeOrderCount) || 0;
  const changeOrderAmt = amountOrDash(position?.changeOrderAmt);
  const hasChangeOrders =
    changeOrderCount > 0 || Number(position?.changeOrderAmt || 0) !== 0;
  const originalAmt = amountOrDash(position?.originalAmt);
  const revisedAmt = amountOrDash(position?.revisedAmt);
  // `changeOrdersUnavailable` is a genuine gap: a refused change-order read
  // used to be indistinguishable from a commitment that genuinely has none,
  // which is how a wrong remaining figure went unexplained. It is advisory —
  // additive, never replacing the figure.
  //
  // `lineSumExceedsRevised` is NOT advisory any more: the backend folds
  // Foundation's detail-line total into Revised contract / Remaining whenever
  // it exceeds header + synced change orders, so the figures above are
  // already correct. This note only explains why — and, since that can
  // happen with no synced change order at all (Zernco's case: the explicit
  // change-order read found nothing, but the raised amount was already in the
  // lines), it also decides whether to show the breakdown below, not just
  // `hasChangeOrders`.
  const changeOrdersUnavailable = position?.changeOrdersUnavailable === true;
  const lineSumExceedsRevised = position?.lineSumExceedsRevised === true;
  const lineSumAmt = amountOrDash(position?.lineSumAmt);
  const showCommitmentBreakdown = hasChangeOrders || lineSumExceedsRevised;
  const asOf = position?.erpConsumptionSyncedAt
    ? new Date(position.erpConsumptionSyncedAt).toLocaleDateString()
    : '';

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '.3rem 1.1rem',
        padding: '.5rem .7rem',
        marginTop: '.6rem',
        borderRadius: '8px',
        backgroundColor: Colors.Canvas_Left_BG
      }}>
      <Figure
        label={labels.ERP_REMAINING}
        value={remaining === null ? labels.NOT_KNOWN : remaining}
        emphasis
      />
      {/* The reason is rendered as TEXT, not only as a tooltip. A bare dash
          sends the reader to ask someone why — which is exactly what happened
          the first time this shipped. */}
      {remaining === null && reason ? (
        <Typography
          sx={{ color: Colors.TEXTGREY, fontSize: '.72rem', fontStyle: 'italic' }}>
          {reason}
        </Typography>
      ) : null}
      {received !== null ? (
        <Figure
          label={labels.ERP_RECEIVED}
          value={received}
          hint={
            position?.erpInvoiceCount
              ? `${position.erpInvoiceCount} ERP invoice(s)`
              : ''
          }
        />
      ) : null}
      {/* Retainage is reported, never netted off the consumption: an invoice
          commits the full contracted amount, and the withheld portion is
          released later against that same invoice rather than as new spend. */}
      {retainage !== null && Number(position?.erpRetainageAmt) !== 0 ? (
        <Figure label={labels.ERP_RETAINAGE} value={retainage} />
      ) : null}
      {/* Beiing Human's own side, shown BESIDE the ERP's and never merged with
          it. The gap between the two is the useful part: a customer's older
          invoices live in the ERP and were never processed here, while an
          invoice approved here and not yet exported has moved only this side. */}
      {bhRemaining !== null ? (
        <Figure label={labels.BH_REMAINING} value={bhRemaining} emphasis />
      ) : null}
      {bhReceived !== null ? (
        <Figure label={labels.BH_RECEIVED} value={bhReceived} />
      ) : null}
      {showCommitmentBreakdown && originalAmt !== null ? (
        <Figure label={labels.ORIGINAL_CONTRACT} value={originalAmt} />
      ) : null}
      {hasChangeOrders && changeOrderAmt !== null ? (
        <Figure
          label={`${labels.CHANGE_ORDERS} (${changeOrderCount})`}
          value={changeOrderAmt}
        />
      ) : null}
      {showCommitmentBreakdown && revisedAmt !== null ? (
        <Figure label={labels.REVISED_CONTRACT} value={revisedAmt} />
      ) : null}
      {changeOrdersUnavailable ? (
        <Typography
          sx={{ color: Colors.TEXTGREY, fontSize: '.72rem', fontStyle: 'italic' }}>
          {labels.CHANGE_ORDERS_UNAVAILABLE}
        </Typography>
      ) : null}
      {lineSumExceedsRevised ? (
        <Typography
          sx={{ color: Colors.TEXTGREY, fontSize: '.72rem', fontStyle: 'italic' }}>
          {lineSumAmt
            ? `${labels.LINE_SUM_EXCEEDS} (lines total ${lineSumAmt})`
            : labels.LINE_SUM_EXCEEDS}
        </Typography>
      ) : null}
      {asOf ? (
        <Typography sx={{ color: Colors.TEXTGREY, fontSize: '.68rem' }}>
          {`${labels.AS_OF} ${asOf}`}
        </Typography>
      ) : null}
    </Box>
  );
};

export default CommitmentPositionPanel;
